import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sparkles, Lock } from "lucide-react";
import googlePlayBadge from "@/assets/google-play-badge.png";

const APP_STORE_BADGE = "https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg";
const GOOGLE_PLAY_BADGE = googlePlayBadge;

export const GOOGLE_PLAY_URL = "https://play.google.com/store/apps/details?id=com.atheniaai.decorcanvas";
export const APP_STORE_URL = "https://apps.apple.com/in/app/decor-canvas/id6758079242";
export const WEB_DESIGNER_PATH = "/designer";

/** Every button in the row uses this exact box, so they all match in height and width on
 *  both desktop and mobile web (the mobile stack stays centered instead of stretching). */
const CELL = "h-11 w-[158px]";

interface Props {
  className?: string;
  /** Admin editor preview: shows a locked label and disables navigation. */
  preview?: boolean;
  /** Bottom-of-article placement: adds an inviting line above the buttons. */
  withIntro?: boolean;
}

const BlogAppAccess = ({ className = "", preview = false, withIntro = false }: Props) => {
  const navigate = useNavigate();

  // Same layout on web and inside the Android/iOS apps.
  const intro = withIntro ? (
    <div className="mb-4">
      <h2 className="text-xl font-bold text-foreground mb-1.5">Try these ideas on your own room</h2>
      <p className="text-sm text-muted-foreground leading-relaxed">
        Upload a photo of your space and see it restyled in seconds — free to start, no design skills needed.
      </p>
    </div>
  ) : null;

  const wrap = (children: React.ReactNode) => (
    <div className={`${preview ? "rounded-xl border border-dashed border-border p-3" : ""} ${className}`}>
      {preview && (
        <p className="flex items-center gap-1 text-xs text-muted-foreground mb-2">
          <Lock className="h-3 w-3" /> Added automatically to every post
        </p>
      )}
      {intro}
      {children}
    </div>
  );

  const stop = preview ? (e: React.MouseEvent) => e.preventDefault() : undefined;

  return wrap(
    // Left-aligned row. On phones the invisible full-width break puts the two store
    // badges side by side and drops the coral button onto the row below.
    <div className="flex flex-wrap items-center gap-2.5">
      <a
        href={GOOGLE_PLAY_URL}
        target="_blank"
        rel="noopener noreferrer"
        onClick={stop}
        aria-label="Get Decor Canvas on Google Play"
        className={`${CELL} transition-transform hover:-translate-y-0.5`}
      >
        <img
          src={GOOGLE_PLAY_BADGE}
          alt="Get it on Google Play"
          className="h-full w-full object-contain"
        />
      </a>
      <a
        href={APP_STORE_URL}
        target="_blank"
        rel="noopener noreferrer"
        onClick={stop}
        aria-label="Download Decor Canvas on the App Store"
        className={`${CELL} transition-transform hover:-translate-y-0.5`}
      >
        <img
          src={APP_STORE_BADGE}
          alt="Download on the App Store"
          className="h-full w-full object-contain"
        />
      </a>
      {/* Mobile: the coral button drops to its own left-aligned row with room for the
          full label. Desktop (sm+): the wrapper dissolves (display:contents) so the
          button sits inline in the row exactly as before, same 158×44 box. */}
      <div className="mt-2 flex w-full justify-start sm:mt-0 sm:contents">
        <Button
          size="lg"
          className="h-11 w-auto min-w-[180px] px-4 gap-1 text-[13px] sm:w-[158px] sm:min-w-0 sm:px-1.5"
          onClick={() => !preview && navigate(WEB_DESIGNER_PATH)}
        >
          <Sparkles className="h-3.5 w-3.5" /> Open Web Designer
        </Button>
      </div>
    </div>
  );
};

export default BlogAppAccess;
